import { z } from 'zod';

/**
 * 等級 (Tier) 表單 schema — 對齊後端 `tier.dto.ts` 的 CreateTierDTO / UpdateTierDTO。
 * 後續若導入 nestjs-zod / OpenAPI codegen，這份會由後端產出，前端改為 import。
 */
export const TierUpsertSchema = z
  .object({
    name: z
      .string()
      .min(1, '請輸入等級名稱')
      // 對齊後端 CreateTierDTO/UpdateTierDTO `RuleType.string().max(50)`（tier.dto.ts:7/95）+
      // entity vpn_name length。先前前端 max(64) 寬於後端 → 名稱 51-64 字前端 zod 過、後端
      // 422 reject，且 ACL 欄位用裸 Controller zod 錯誤不顯示 → user 誤以為「按了沒反應」(#3)。
      .max(50, '名稱不可超過 50 個字元'),
    description: z
      .string()
      .max(255, '描述不可超過 255 個字元')
      .optional()
      .or(z.literal('')),
    level: z
      .number({ message: '請輸入等級數值' })
      .int('等級必須為整數')
      .min(0, '等級不能為負數')
      .max(999, '等級不可超過 999'),
    maxDevices: z
      .number({ message: '請輸入最大裝置數' })
      .int('裝置數必須為整數')
      .min(1, '至少 1 個裝置')
      .max(100, '最多 100 個裝置'),
    downloadLimitMbps: z
      .number()
      .int('速度必須為整數 Mbps')
      .min(0, '下載速度不能為負數')
      .optional(),
    uploadLimitMbps: z
      .number()
      .int('速度必須為整數 Mbps')
      .min(0, '上傳速度不能為負數')
      .optional(),
    dailyTrafficMb: z
      .number()
      .int('流量必須為整數 MB')
      .min(0, '每日流量不能為負數')
      .optional(),
    sessionDurationMinutes: z
      .number()
      .int('連線時長必須為整數分鐘')
      .min(0, '連線時長不能為負數')
      .optional(),
    sortOrder: z
      .number({ message: '請輸入排序值' })
      .int('排序必須為整數')
      .min(0, '排序不能為負數'),
    isFree: z.boolean().default(true),
    canAccessVless: z.boolean().default(false),
    /**
     * Sprint 21 / W6-B — DAITA UNION ACL opt-in.
     * When true, DAITA-enabled nodes (node config `daita.enabled=true`,
     * authored by W6-A) are UNION'd into the tier-visible set regardless
     * of the `allowedNodeIds` whitelist (analog to canAccessVless).
     * Default false — Sprint 22 product decision will decide which tiers
     * get this flag, never auto-promoted by this schema.
     */
    canAccessDaita: z.boolean().default(false),
    priceMonthly: z
      .number()
      .min(0, '月費不能為負數')
      .nullable()
      .optional(),
    priceYearly: z
      .number()
      .min(0, '年費不能為負數')
      .nullable()
      .optional(),
    /**
     * W3-G (sprint19, Sprint 21 F1 fold) — tri-state tier ACL widget.
     * - 'all'      → `allowedNodeIds=null`  (allow-all, legacy default)
     * - 'select'   → `allowedNodeIds=[...]` (explicit whitelist)
     * - 'deny-all' → `allowedNodeIds=[]`    (deny-all explicit lockdown)
     *
     * The submit handler in `TierFormModal` translates this enum into the
     * canonical `allowedNodeIds` payload + the required `confirmDenyAll`
     * confirmation flag (server-side gate guarantees a deny-all save can
     * never happen by accidental UI click).
     */
    allowedNodesMode: z.enum(['all', 'select', 'deny-all']).default('all'),
    allowedNodeIds: z
      .array(z.number().int().positive())
      .default([]),
  })
  .refine(
    (v) => {
      // 付費方案必須填月費或年費（至少一個）
      if (!v.isFree && !v.priceMonthly && !v.priceYearly) return false;
      return true;
    },
    {
      message: '付費方案請至少填寫月費或年費',
      path: ['priceMonthly'],
    }
  );

export type TierUpsertDTO = z.infer<typeof TierUpsertSchema>;

/**
 * 等級排程 schema — 排程是獨立的後端資源（POST /tiers/:id/schedules），
 * 不嵌入 tier 主 form。建立和編輯共用同一個 schema。
 */
export const TierScheduleSchema = z
  .object({
    dayOfWeek: z
      .number()
      .int()
      .min(0, '星期必須在 0-6')
      .max(6, '星期必須在 0-6')
      .nullable()
      .optional(),
    startTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, '請輸入有效開始時間'),
    endTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, '請輸入有效結束時間'),
    downloadPercent: z
      .number({ message: '請輸入下載比例' })
      .int()
      .min(0, '下載比例不能小於 0')
      .max(100, '下載比例不能超過 100'),
    uploadPercent: z
      .number({ message: '請輸入上傳比例' })
      .int()
      .min(0, '上傳比例不能小於 0')
      .max(100, '上傳比例不能超過 100'),
    priority: z
      .number({ message: '請輸入優先序' })
      .int()
      .min(0, '優先序不能為負數'),
  })
  .refine((v) => v.startTime !== v.endTime, {
    message: '開始時間不可等於結束時間',
    path: ['endTime'],
  });

export type TierScheduleDTO = z.infer<typeof TierScheduleSchema>;
